import i18next from "i18next";
import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import { PRODUCT_PENDING_ERROR_CODE } from "../models/mappers/mapError";
import { showInfoToast } from "../../presentation/components/Toast/Toast";

// 409 productPending handling (flag REACT_APP_PRODUCT_PENDING_ENABLED).
//
// The API's ProductPendingGuard sits in front of every commit path (logo
// commit / approval-start / verdict, design commit / approval-start /
// verdict, order commit / place). It waits at most 3 s on the worker's SQL
// result and otherwise answers 409 { code: "productPending", error:
// "productPending", message: "Prices are being updated…", logoIds,
// retryAfterSeconds } (ProductPendingGuardPolicy.ToHttpBody). authFetch turns
// that into a DataError whose errorStatus carries the HTTP status (409); under
// the flag errorStatusFromBody keeps the marker as errorCode and the
// retryAfterSeconds (the string `code` itself is replaced by the HTTP status
// and `message` never names the marker). A marker in the message / raw JSON
// text (bodies without a message) is recognised as well.
//
// Policy (owner): one info toast + ONE automatic retry after retryAfter
// (default 3 s, clamped 1-10 s). A second 409 - or any other error - is
// rethrown untouched so the caller's withToasts shows it as today.
// Flag off = `run()` is returned as is: no catch, no toast, no retry.

export { PRODUCT_PENDING_ERROR_CODE };
export const PRODUCT_PENDING_RETRY_DELAY_MS = 3_000;
const MIN_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 10_000;

function mentionsProductPending(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.toLowerCase().includes(PRODUCT_PENDING_ERROR_CODE.toLowerCase())
  );
}

export function isProductPendingError(error: unknown): boolean {
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 409) return false;
  return (
    mentionsProductPending(status.errorCode) ||
    mentionsProductPending(status.message) ||
    mentionsProductPending(error.message)
  );
}

// retryAfterSeconds from the 409 envelope - carried on the ErrorStatus by
// errorStatusFromBody, or read from the raw JSON body when that survived as
// the message; otherwise the default. Clamped so a bad value can neither
// hammer the API nor park the click for a minute.
export function productPendingRetryDelayMs(error: unknown): number {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const carried = Number(status?.retryAfterSeconds);
  if (Number.isFinite(carried) && carried > 0) {
    return Math.min(
      MAX_RETRY_DELAY_MS,
      Math.max(MIN_RETRY_DELAY_MS, Math.round(carried * 1000))
    );
  }
  const message = status?.message;
  if (typeof message === "string" && message.trim().startsWith("{")) {
    try {
      const body = JSON.parse(message) as { retryAfterSeconds?: unknown };
      const seconds = Number(body.retryAfterSeconds);
      if (Number.isFinite(seconds) && seconds > 0) {
        return Math.min(
          MAX_RETRY_DELAY_MS,
          Math.max(MIN_RETRY_DELAY_MS, Math.round(seconds * 1000))
        );
      }
    } catch {
      // Not a JSON body after all - use the default.
    }
  }
  return PRODUCT_PENDING_RETRY_DELAY_MS;
}

// Marker of the clone-upload guard's 409 (flag cloneInstantEnabled): the
// same envelope with reason "cloneUpload" - a clone still awaiting its own
// supplier identity. Only the toast text differs.
export const CLONE_UPLOAD_REASON = "cloneUpload";

export function isCloneUploadPendingError(error: unknown): boolean {
  if (!enviroment.cloneInstantEnabled) return false;
  if (!isProductPendingError(error)) return false;
  const status = (error as DataError).errorStatus;
  return status?.reason === CLONE_UPLOAD_REASON;
}

function defaultNotify(error?: unknown): void {
  showInfoToast(
    i18next.t(
      isCloneUploadPendingError(error)
        ? "upload_pending_retry"
        : "product_pending_retry"
    )
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface ProductPendingRetryOptions {
  // Replaces the info toast (tests, silent callers).
  notify?: () => void;
  // Told about the 409 the retry is about to answer (after the toast, before
  // the wait) - a caller that must know its answer is the RETRY's answer
  // (LogoRepository.initiateApproval: the approval start's scale-down
  // happened in the first request, so the retry's answer carries no
  // maxSizeClamp). Never called when the first call succeeds or fails
  // with anything else.
  onRetry?: (error: unknown) => void;
}

export async function withProductPendingRetry<T>(
  run: () => Promise<T>,
  options?: ProductPendingRetryOptions
): Promise<T> {
  // Flag cloneInstantEnabled: the clone-upload guard sits on the same
  // commit / approval / order paths and answers the same envelope, so the
  // retry is armed on that flag as well (K3 may be on without R5).
  if (!enviroment.productPendingEnabled && !enviroment.cloneInstantEnabled) {
    return run();
  }
  try {
    return await run();
  } catch (error) {
    if (!isProductPendingError(error)) throw error;
    if (options?.notify) options.notify();
    else defaultNotify(error);
    options?.onRetry?.(error);
    await wait(productPendingRetryDelayMs(error));
    return run();
  }
}

// The colour / resize / method-change mutations only ever meet the
// clone-upload guard (a product refresh never blocks them), so they retry
// under cloneInstantEnabled alone - flag off = `run()` as is, untouched by
// the product-pending flag.
export function withCloneUploadRetry<T>(
  run: () => Promise<T>,
  options?: ProductPendingRetryOptions
): Promise<T> {
  if (!enviroment.cloneInstantEnabled) return run();
  return withProductPendingRetry(run, options);
}
